// Markdown parsing utilities using marked with custom renderers
// Provides custom rendering for code blocks, tables, and images
// KaTeX auto-render for LaTeX math ($...$ and $$...$$)

import { marked } from 'marked';
import renderMathInElement from 'katex/contrib/auto-render';

/**
 * Create a custom code block with language header and copy button
 * @param {string} code - The code content
 * @param {string} language - The code language (optional)
 * @returns {string} HTML string for the custom code block
 */
function createCustomCodeBlock(code, language = '') {
  const languageLabel = language || 'code';
  const codeId = `code-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  return `
    <div class="markdown-code-block">
      <div class="code-header">
        <span class="language-label">${languageLabel}</span>
        <button class="code-copy-button" data-code-id="${codeId}" title="Copy code">
          <span class="code-copy-icon code-copy-icon--copy" aria-hidden="true">
            <img class="code-copy-icon-img" src="/svgs/clipboard.svg" alt="" />
          </span>
          <span class="code-copy-icon code-copy-icon--check" aria-hidden="true">
            <img class="code-copy-icon-img" src="/svgs/check.svg" alt="" />
          </span>
        </button>
      </div>
      <pre><code class="language-${language}" id="${codeId}">${escapeHtml(code)}</code></pre>
    </div>
  `;
}

/**
 * Create a custom table with custom styling
 * @param {string} header - Table header HTML
 * @param {string} body - Table body HTML
 * @returns {string} HTML string for the custom table
 */
function createCustomTable(header, body) {
  return `
    <div class="markdown-table-wrapper">
      <table class="markdown-table">
        <thead>${header}</thead>
        <tbody>${body}</tbody>
      </table>
    </div>
  `;
}

/**
 * Create a custom image with error handling and placeholder
 * @param {string} href - Image source URL
 * @param {string} title - Image title/alt text
 * @param {string} text - Image alt text
 * @returns {string} HTML string for the custom image
 */
function createCustomImage(href, title, text) {
  const imageId = `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  return `
    <div class="markdown-image-wrapper">
      <img 
        src="${escapeHtml(href)}" 
        alt="${escapeHtml(text || title || '')}" 
        title="${escapeHtml(title || '')}"
        id="${imageId}"
        onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';"
        loading="lazy"
      />
      <div class="markdown-image-placeholder" style="display:none;">
        <span class="placeholder-icon">🖼️</span>
        <span class="placeholder-text">Image failed to load</span>
      </div>
    </div>
  `;
}

/**
 * Escape HTML to prevent XSS
 * @param {string} text - Text to escape
 * @returns {string} Escaped HTML
 */
function escapeHtml(text) {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Protect LaTeX math delimiters from being consumed by the markdown parser.
 * We temporarily replace math blocks/spans with placeholders, run markdown,
 * then restore the original math so KaTeX can process it afterwards.
 *
 * We intentionally skip fenced code blocks so examples like ```latex ... ```
 * remain untouched.
 * @param {string} markdown
 * @returns {{ text: string, placeholders: Array<{ key: string, value: string }> }}
 */
function protectMathExpressions(markdown) {
  if (!markdown || typeof markdown !== 'string') {
    return { text: markdown || '', placeholders: [] };
  }

  const placeholders = [];
  let index = 0;
  const createPlaceholder = (value) => {
    const key = `REVOLT_MATH_PLACEHOLDER_${index++}`;
    placeholders.push({ key, value });
    return key;
  };

  const fencedCodeBlockRegex = /(```[\s\S]*?```)/g;
  const mathPatterns = [
    /\\\[[\s\S]*?\\\]/g, // display math: \[ ... \]
    /\\\([\s\S]*?\\\)/g, // inline math: \( ... \)
    /\$\$[\s\S]*?\$\$/g, // display math: $$ ... $$
  ];

  const protectedText = markdown
    .split(fencedCodeBlockRegex)
    .map((segment) => {
      if (/^```[\s\S]*```$/.test(segment)) {
        return segment;
      }

      return mathPatterns.reduce((text, pattern) => {
        return text.replace(pattern, (match) => createPlaceholder(match));
      }, segment);
    })
    .join('');

  return { text: protectedText, placeholders };
}

/**
 * Restore protected LaTeX expressions into generated HTML so KaTeX sees them.
 * @param {string} html
 * @param {Array<{ key: string, value: string }>} placeholders
 * @returns {string}
 */
function restoreMathExpressions(html, placeholders) {
  if (!html || !Array.isArray(placeholders) || placeholders.length === 0) {
    return html || '';
  }

  return placeholders.reduce((result, { key, value }) => {
    return result.split(key).join(escapeHtml(value));
  }, html);
}

/** Match #RGB or #RRGGBB (not #RRGGBBAA); longer form preferred by alternation order */
const HEX_COLOR_IN_TEXT_RE =
  /#([0-9a-fA-F]{6})(?![0-9a-fA-F])|#([0-9a-fA-F]{3})(?![0-9a-fA-F])/g;

/**
 * @param {string} hexDigits - 3 or 6 hex chars (no #)
 * @returns {string} Normalized #rrggbb for CSS
 */
function hexDigitsToCssColor(hexDigits) {
  const h = hexDigits;
  if (h.length === 3) {
    return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toLowerCase();
  }
  return `#${h.toLowerCase()}`;
}

/**
 * @param {Text} textNode
 * @returns {boolean}
 */
function shouldSkipTextNodeForHexSwatches(textNode) {
  const parent = textNode.parentElement;
  if (!parent) return true;
  // Skip fenced code only (`<pre><code>`). Inline `<code>` (backticks) often wraps hex colors—process those.
  if (parent.closest("pre, script, style, noscript")) return true;
  return false;
}

/**
 * After markdown → HTML, wrap #RGB / #RRGGBB in prose with a small swatch (not inside fenced `pre` blocks).
 * @param {string} html
 * @returns {string}
 */
function injectHexColorSwatches(html) {
  if (!html || typeof html !== "string" || html.indexOf("#") === -1) {
    return html;
  }
  if (typeof DOMParser === "undefined") {
    return html;
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(
      `<div class="md-swatch-root">${html}</div>`,
      "text/html"
    );
    const root = doc.body.querySelector(".md-swatch-root");
    if (!root) {
      return html;
    }

    const textNodes = [];
    const tw = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    let n;
    while ((n = tw.nextNode())) {
      textNodes.push(n);
    }

    for (const textNode of textNodes) {
      if (shouldSkipTextNodeForHexSwatches(textNode)) continue;

      const text = textNode.textContent;
      if (!text || text.indexOf("#") === -1) continue;

      const parts = [];
      let lastIndex = 0;
      let m;
      HEX_COLOR_IN_TEXT_RE.lastIndex = 0;
      while ((m = HEX_COLOR_IN_TEXT_RE.exec(text)) !== null) {
        parts.push({ type: "text", value: text.slice(lastIndex, m.index) });
        const hexDigits = m[1] || m[2];
        parts.push({ type: "swatch", hexDigits, full: m[0] });
        lastIndex = m.index + m[0].length;
      }
      parts.push({ type: "text", value: text.slice(lastIndex) });

      if (parts.length === 1) continue;

      const frag = doc.createDocumentFragment();
      for (const p of parts) {
        if (p.type === "text") {
          frag.appendChild(doc.createTextNode(p.value));
        } else {
          const wrap = doc.createElement("span");
          wrap.className = "md-color-swatch-wrap";
          const sw = doc.createElement("span");
          sw.className = "md-color-swatch";
          sw.style.backgroundColor = hexDigitsToCssColor(p.hexDigits);
          sw.setAttribute("aria-hidden", "true");
          const label = doc.createElement("span");
          label.className = "md-color-swatch-label";
          label.textContent = p.full;
          wrap.appendChild(sw);
          wrap.appendChild(label);
          frag.appendChild(wrap);
        }
      }
      textNode.parentNode.replaceChild(frag, textNode);
    }

    return root.innerHTML;
  } catch (error) {
    if (window.logger) {
      window.logger.warn(
        {
          tags: "utils|markdown|swatch",
          color1: "yellow",
          includeSource: true,
        },
        "injectHexColorSwatches failed:",
        error
      );
    }
    return html;
  }
}

/**
 * Initialize marked with custom renderers
 */
function initializeMarked() {
  const renderer = new marked.Renderer();

  // Custom code block renderer
  renderer.code = (code, language) => {
    return createCustomCodeBlock(code, language);
  };

  // Custom table renderer
  renderer.table = (header, body) => {
    return createCustomTable(header, body);
  };

  // Custom image renderer
  renderer.image = (href, title, text) => {
    return createCustomImage(href, title, text);
  };

  // Configure marked with custom renderer
  marked.setOptions({
    renderer: renderer,
    breaks: true, // Convert line breaks to <br>
    gfm: true, // GitHub Flavored Markdown
  });
}

// Initialize marked on module load
initializeMarked();

/**
 * Parse markdown string to HTML with custom renderers
 * @param {string} markdown - Markdown string to parse
 * @returns {string} Parsed HTML string
 */
export function parseMarkdown(markdown) {
  if (!markdown || typeof markdown !== 'string') {
    return '';
  }

  try {
    const { text: protectedMarkdown, placeholders } = protectMathExpressions(markdown);
    const html = marked.parse(protectedMarkdown);
    const restoredHtml = restoreMathExpressions(html, placeholders);
    const withSwatches = injectHexColorSwatches(restoredHtml);
    // Wrap in a stable root class so markdown styling can be applied globally (lists, spacing, etc.)
    // without needing page-specific container selectors.
    return `<div class="revolt-markdown">${withSwatches}</div>`;
  } catch (error) {
    if (window.logger) {
      window.logger.error({
        tags: "utils|markdown|parse",
        color1: "red",
        color2: "orange",
        includeSource: true
      }, "Failed to parse markdown:", error);
    }
    // Return escaped HTML as fallback
    return `<div class="revolt-markdown">${escapeHtml(markdown)}</div>`;
  }
}

/**
 * Set up copy button event listeners for code blocks
 * This should be called after rendering markdown content to DOM
 * @param {Element} container - Container element containing code blocks
 */
export function setupCodeBlockCopyButtons(container) {
  if (!container) return;

  const copyButtons = container.querySelectorAll('.code-copy-button');
  
  copyButtons.forEach(button => {
    // Remove existing listeners to prevent duplicates
    const newButton = button.cloneNode(true);
    button.parentNode.replaceChild(newButton, button);
    
    newButton.addEventListener('click', async () => {
      const codeId = newButton.getAttribute('data-code-id');
      const codeElement = container.querySelector(`#${codeId}`);
      
      if (codeElement) {
        const codeText = codeElement.textContent || codeElement.innerText;
        
        try {
          const copyOk = await (async () => {
            // Preferred path: async Clipboard API (requires secure context/permissions)
            try {
              if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(codeText);
                return true;
              }
            } catch (_) {}

            // Fallback: textarea + execCommand (works in more contexts)
            try {
              const ta = document.createElement('textarea');
              ta.value = codeText;
              ta.setAttribute('readonly', '');
              ta.style.position = 'fixed';
              ta.style.left = '-9999px';
              ta.style.top = '0';
              document.body.appendChild(ta);
              ta.select();
              const ok = document.execCommand && document.execCommand('copy');
              document.body.removeChild(ta);
              return !!ok;
            } catch (_) {
              return false;
            }
          })();

          if (!copyOk) {
            // No UI feedback if copy fails; keep behavior consistent.
            return;
          }
          
          // Visual feedback (toggle icon, keep button dimensions fixed)
          newButton.classList.add('is-copied');
          newButton.style.opacity = '0.7';
          
          setTimeout(() => {
            newButton.classList.remove('is-copied');
            newButton.style.opacity = '1';
          }, 2000);
        } catch (error) {
          if (window.logger) {
            window.logger.warn({
              tags: "utils|markdown|copy",
              color1: "yellow",
              includeSource: true
            }, "Failed to copy code to clipboard:", error);
          }
        }
      }
    });
  });
}

/**
 * Check if a URL is external (different origin)
 * @param {string} url - URL to check
 * @returns {boolean} True if URL is external
 */
function isExternalUrl(url) {
  if (!url) return false;
  
  try {
    const linkUrl = new URL(url, window.location.href);
    const currentUrl = new URL(window.location.href);
    
    // Check if origins match
    return linkUrl.origin !== currentUrl.origin;
  } catch (error) {
    // If URL parsing fails, treat as external to be safe
    return true;
  }
}

/** KaTeX auto-render options for LaTeX math. */
const KATEX_OPTIONS = {
  delimiters: [
    { left: '$$', right: '$$', display: true },
    // IMPORTANT: we intentionally do NOT support `$...$` inline math because it conflicts heavily
    // with normal currency formatting in chat (e.g. "$200 000"), which can cause KaTeX to try
    // to parse large spans of text as math and mangle the rendered output.
    { left: '\\[', right: '\\]', display: true },
    { left: '\\(', right: '\\)', display: false },
  ],
  ignoredTags: ['script', 'noscript', 'style', 'textarea', 'pre', 'code'],
  throwOnError: false,
};

/**
 * Render LaTeX math in a DOM element (runs during streaming and on final render)
 * Call after setting innerHTML with markdown content
 * @param {Element} container - Container element containing math delimiters
 */
export function renderMath(container) {
  if (!container) return;
  try {
    renderMathInElement(container, KATEX_OPTIONS);
  } catch (error) {
    if (window.logger) {
      window.logger.warn({
        tags: 'utils|markdown|katex',
        color1: 'yellow',
        includeSource: true,
      }, 'KaTeX render failed:', error);
    }
  }
}

/**
 * Set up link click handlers to intercept external links
 * This should be called after rendering markdown content to DOM
 * @param {Element} container - Container element containing links
 */
export function setupExternalLinkHandlers(container) {
  if (!container) return;

  const links = container.querySelectorAll('a');
  
  links.forEach(link => {
    // Remove existing listeners to prevent duplicates
    const newLink = link.cloneNode(true);
    link.parentNode.replaceChild(newLink, link);
    
    newLink.addEventListener('click', (e) => {
      const href = newLink.getAttribute('href');
      
      if (!href) {
        e.preventDefault();
        return;
      }
      
      // Check if link is external
      if (isExternalUrl(href)) {
        e.preventDefault();
        e.stopPropagation();
        
        // Dispatch custom event to show modal
        window.dispatchEvent(new CustomEvent('external-link:click', {
          detail: { url: href }
        }));
      }
      // Internal links will work normally (React Router will handle them)
    });
  });
}

/**
 * Parse JSON string with syntax highlighting
 * @param {string} jsonString - JSON string to parse and highlight
 * @returns {string} HTML string with syntax highlighting
 */
export function parseJson(jsonString) {
  if (!jsonString || typeof jsonString !== 'string') {
    return '';
  }

  try {
    // Parse and re-stringify to ensure valid JSON and pretty formatting
    const parsed = JSON.parse(jsonString);
    const prettyJson = JSON.stringify(parsed, null, 2);

    // Apply syntax highlighting
    return highlightJson(prettyJson);
  } catch (error) {
    if (window.logger) {
      window.logger.warn({
        tags: "utils|json|parse",
        color1: "yellow",
        includeSource: true
      }, "Failed to parse JSON:", error);
    }
    // Return escaped text as fallback
    return `<span class="json-error">Invalid JSON: ${escapeHtml(jsonString)}</span>`;
  }
}

/**
 * Apply syntax highlighting to JSON string
 * @param {string} json - Pretty-printed JSON string
 * @returns {string} HTML with syntax highlighting
 */
function highlightJson(json) {
  if (!json) return '';

  return json.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?|[{}]|\[|\]|,)/g,
    function (match) {
      let cls = 'json-number';
      if (/^"/.test(match)) {
        if (/:$/.test(match)) {
          cls = 'json-key';
        } else {
          cls = 'json-string';
        }
      } else if (/true|false|null/.test(match)) {
        cls = 'json-keyword';
      } else if (match === '{' || match === '}') {
        cls = 'json-brace';
      } else if (match === '[' || match === ']') {
        cls = 'json-bracket';
      } else if (match === ',') {
        cls = 'json-comma';
      }

      if (cls === 'json-key') {
        const key = match.trim().replace(/:$/, '');
        return `<span class="${cls}">${key}</span><span class="json-colon">:</span>`;
      }
      return `<span class="${cls}">${match}</span>`;
    }
  );
}

/**
 * Markdown utilities object
 */
export const markdownUtils = {
  parse: parseMarkdown,
  parseJson: parseJson,
  setupCopyButtons: setupCodeBlockCopyButtons,
  setupExternalLinks: setupExternalLinkHandlers,
  renderMath,
};

